import type { FeatureLike } from 'ol/Feature';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import * as proj from 'ol/proj';
import type { StyleLike } from 'ol/style/Style';
import type Style from 'ol/style/Style';

import { isNonEmptyString } from '@nora-monorepo/shared';

import { markerFeatureStyle } from '#nora-ui/map/feature-styles/marker-feature-style';
import { ClusterableLayer } from '#nora-ui/map/map/layer-managers/ClusterableLayer';
import type { NoraMap } from '#nora-ui/map/map/NoraMap';
import type { NoraMapMarker } from '#nora-ui/map/types/marker.types';

export class MarkerLayerManager extends ClusterableLayer {
  readonly LAYER_ID = 'markerLayer';
  readonly LAYER_NAME = 'NORA Markers';
  override readonly isClickable = true;

  constructor(map: NoraMap) {
    super(map);
  }

  override getStyleForSingleFeature(feature: FeatureLike): Style[] | Style {
    if (!(feature instanceof Feature)) {
      return this.getEmptyStyle();
    }

    const style = feature.getStyle();

    if (typeof style === 'function') {
      const resolution = this.map.getResolution();
      const result = style(feature, resolution);

      return result ?? this.getEmptyStyle();
    }

    return style ?? this.getEmptyStyle();
  }

  /**
   * Load/reload marker layer features from state.
   * Clears existing features before loading.
   */
  override load(): void {
    this.unload();

    const markers = this.map.state.marker.getAllMarkers();

    markers.forEach((marker) => {
      this.addMarkerFeature(marker);
    });
  }

  /**
   * Unload/clear all marker layer features.
   */
  override unload(): void {
    this.source.clear();
  }

  updateOrAddMarkerFeature(marker: NoraMapMarker): void {
    const existingFeature = this.getMarkerFeatureById(marker.id);

    if (existingFeature) {
      this.updateMarkerFeature(marker);
    }
    else {
      this.addMarkerFeature(marker);
    }
  }

  updateMarkerFeature(marker: NoraMapMarker): void {
    const existingFeature = this.getMarkerFeatureById(marker.id);

    if (!existingFeature) {
      return;
    }

    const newFeature = this.createFeatureFromMarker(marker);

    existingFeature.setGeometry(newFeature.getGeometry());
    existingFeature.setStyle(newFeature.getStyle());

    this.setFeatureMetadata(existingFeature, marker);
  }

  addMarkerFeature(marker: NoraMapMarker): void {
    const feature = this.createFeatureFromMarker(marker);

    this.setFeatureMetadata(feature, marker);

    this.source.addFeature(feature);
  }

  removeMarkerFeature(markerId: string): void {
    const feature = this.getMarkerFeatureById(markerId);

    if (!feature) {
      return;
    }

    this.source.removeFeature(feature);
  }

  getMarkerFeatureById(markerId: string): Feature | undefined {
    return this.source.getFeatureById(markerId) ?? undefined;
  }

  private setFeatureMetadata(feature: Feature, marker: NoraMapMarker): void {
    feature.set('markerId', marker.id);
  }

  private createFeatureFromMarker(marker: NoraMapMarker): Feature {
    const feature = new Feature({
      geometry: new Point(proj.fromLonLat([marker.longitude, marker.latitude])),
    });

    // Enables easy lookup via source.getFeatureById in getMarkerFeatureById.
    feature.setId(marker.id);

    const style = this.getMarkerStyle(marker);

    feature.setStyle(style);

    return feature;
  }

  private getMarkerStyle(marker: NoraMapMarker): StyleLike {
    if (marker.style) {
      return marker.style;
    }

    const displayName
      = isNonEmptyString(marker.name)
        ? marker.name
        : `${marker.latitude}, ${marker.longitude}`;

    return markerFeatureStyle({ icon: 'place', text: displayName });
  }
}
